import {Link} from "react-router-dom";
import {Progress} from "antd";
import className from "classnames/bind";
import {FontAwesomeIcon, Loading} from "~/components";
import {useCan} from "~/hooks";
import GscTrendChart from "~/features/GscInsight/components/GscTrendChart";
import {useGetProjectDashboardQuery} from "~/reduxs/api/projectApiSlice";
import style from "../style/Project.module.scss";

const cn = className.bind(style);

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

/** Ngưỡng "tốt / cần cải thiện / kém" của Core Web Vitals (field data, chuẩn Google). */
const CWV = {
	lcp: {good: 2500, poor: 4000, unit: 'ms', fmt: (v) => `${(v / 1000).toFixed(1)}s`},
	inp: {good: 200, poor: 500, unit: 'ms', fmt: (v) => `${v}ms`},
	cls: {good: 0.1, poor: 0.25, unit: '', fmt: (v) => v.toFixed(2)},
};

const cwvColor = (metric, value) => {
	if (value === null || value === undefined) return '#9ca3af';
	const t = CWV[metric];
	if (value <= t.good) return '#16A34A';
	if (value > t.poor) return '#DC2626';
	return '#D97706';
};

/** Điểm 0–100 → màu (perf/seo...). */
const scoreColor = (s) => (s >= 90 ? '#16A34A' : s >= 50 ? '#D97706' : '#DC2626');

/** Chip % thay đổi. value: number (%) | null ("Mới") | 0 (không đổi). Dương = tốt (BE đã đảo dấu vị trí). */
function Delta({value}) {
	if (value === null) return <span className={cn('delta', 'deltaNew')}>Mới</span>;
	if (!value) return <span className={cn('delta', 'deltaFlat')}>—</span>;
	const up = value > 0;
	return (
		<span className={cn('delta', up ? 'deltaUp' : 'deltaDown')}>
			<FontAwesomeIcon icon={up ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'} />
			{Math.abs(value)}%
		</span>
	);
}

/** Thẻ số liệu nhỏ (giá trị lớn + nhãn). */
function Stat({label, value, icon, color, hint}) {
	return (
		<div className={cn('statCard')}>
			{icon && <div className={cn('statIcon')} style={{color}}><FontAwesomeIcon icon={icon} /></div>}
			<div className={cn('statBody')}>
				<div className={cn('statValue')} style={color ? {color} : undefined}>{value}</div>
				<div className={cn('statLabel')}>{label}</div>
				{hint && <div className={cn('statHint')}>{hint}</div>}
			</div>
		</div>
	);
}

/** Chip có / không (SSL, robots, sitemap). */
const YesNo = ({ok}) => (
	<span className={cn('yesno', ok ? 'yes' : 'no')}>
		<FontAwesomeIcon icon={ok ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'} />
		{ok ? 'Có' : 'Không'}
	</span>
);

function EmptyHint({icon, text, to}) {
	return (
		<div className={cn('emptyHint')}>
			<FontAwesomeIcon icon={icon} />
			<span>{text}</span>
			{to}
		</div>
	);
}

/**
 * Tổng quan một Dự án: gom số liệu từ các tool ĐÃ chạy (Site Crawler, Google Search
 * Console, PageSpeed, Chấm điểm SEO) — điểm SEO + thống kê URL cấp site + Core Web Vitals
 * + xu hướng traffic GSC. Chỉ đọc bản ghi mới nhất mỗi tool; khối nào chưa có dữ liệu thì
 * hiện gợi ý chạy tool tương ứng. Render phía trên lưới công cụ trong ProjectDashboard.
 */
function ProjectOverview({projectId}) {

	const canCrawl = useCan('crawl');
	const canGsc = useCan('gsc');
	const canPageSpeed = useCan('pagespeed');

	const {data, isLoading, isError} = useGetProjectDashboardQuery(projectId);

	if (isLoading) return <div className={cn('card')}><Loading /></div>;
	if (isError || !data) return null;

	const {crawl, gsc, pagespeed, ssl} = data;

	// Link đi sâu ở góc card (gate theo quyền — không có quyền thì chỉ hiện số liệu).
	const deepLink = (to, text) => (
		<Link to={to} className={cn('deepLink')}>
			{text} <FontAwesomeIcon icon="fa-light fa-arrow-right" />
		</Link>
	);

	return (
		<div className={cn('overview')}>

			{/* ---- Điểm SEO tổng thể + thống kê URL (từ Site Crawler) ---- */}
			<h3 className={cn('sectionTitle')}>Tổng quan website</h3>

			{crawl ? (
				<div className={cn('card')}>
					<div className={cn('scorePanel')}>
						<div className={cn('scoreRing')}>
							<Progress
								type="dashboard"
								percent={crawl.score}
								strokeColor={crawl.grade_color}
								format={(p) => (
									<span className={cn('ringValue')}>
										<strong style={{color: crawl.grade_color}}>{p}</strong>
										<small>/100</small>
									</span>
								)}
							/>
							<div className={cn('ringGrade')} style={{color: crawl.grade_color}}>
								Xếp hạng <strong>{crawl.grade}</strong> — {crawl.grade_label}
							</div>
						</div>

						<div className={cn('groupBars')}>
							{crawl.groups.map((g) => (
								<div className={cn('groupBar')} key={g.key}>
									<div className={cn('groupBarHead')}>
										<span>{g.label}</span>
										<span className={cn('groupBarScore')}>{g.score}/100</span>
									</div>
									<Progress percent={g.score} showInfo={false} size="small"
										strokeColor={crawl.grade_color} />
								</div>
							))}
						</div>
					</div>

					<div className={cn('statGrid')}>
						<Stat label="Tổng URL" value={fmt(crawl.counts.total)} icon="fa-light fa-link" color="#4f46e5" />
						<Stat label="URL lỗi" value={fmt(crawl.counts.errors)} icon="fa-light fa-triangle-exclamation"
							color={crawl.counts.errors > 0 ? '#DC2626' : '#16A34A'} />
						<Stat label="Redirect" value={fmt(crawl.counts.redirects)} icon="fa-light fa-arrows-turn-right" color="#D97706" />
						<Stat label="Cho index" value={fmt(crawl.counts.indexable)} icon="fa-light fa-circle-check" color="#16A34A" />
						<Stat label="Noindex" value={fmt(crawl.counts.noindex)} icon="fa-light fa-ban" color="#6b7280" />
						<Stat label="Có canonical" value={fmt(crawl.counts.canonical)} icon="fa-light fa-code-branch" color="#0ea5e9" />
						<Stat label="SSL" value={<YesNo ok={ssl} />} icon="fa-light fa-lock" color={ssl ? '#16A34A' : '#DC2626'} />
						<Stat label="Robots.txt" value={<YesNo ok={crawl.robots} />} icon="fa-light fa-robot" color="#4f46e5" />
						<Stat label="Sitemap" value={<YesNo ok={crawl.sitemap} />} icon="fa-light fa-sitemap" color="#4f46e5" />
					</div>

					<div className={cn('cardFoot')}>
						<span>Cập nhật từ lần crawl {crawl.finished_at || crawl.created}</span>
						{canCrawl && crawl.crawl_id && deepLink(`/crawler/crawls/${crawl.crawl_id}`, 'Xem báo cáo chi tiết')}
					</div>
				</div>
			) : (
				<div className={cn('card')}>
					<EmptyHint icon="fa-light fa-spider"
						text="Chưa có dữ liệu tổng quan. Hãy chạy Site Crawler để chấm điểm SEO và thống kê toàn bộ URL của website."
						to={canCrawl && deepLink(`/projects/${projectId}/crawler`, 'Mở Site Crawler')} />
				</div>
			)}

			{/* ---- Core Web Vitals (từ PageSpeed) ---- */}
			<h3 className={cn('sectionTitle')}>Hiệu năng &amp; Core Web Vitals</h3>
			<div className={cn('card')}>
				{pagespeed ? (
					<>
					<div className={cn('cwvWrap')}>
						<div className={cn('cwvScores')}>
							<Progress type="circle" size={72} percent={pagespeed.perf_score}
								strokeColor={scoreColor(pagespeed.perf_score)} />
							<div className={cn('cwvScoreLabel')}>Hiệu năng (mobile)</div>
						</div>
						<div className={cn('cwvMetrics')}>
							{pagespeed.field.available ? (
								<>
									<Cwv metric="lcp" value={pagespeed.field.lcp} label="LCP" />
									<Cwv metric="inp" value={pagespeed.field.inp} label="INP" />
									<Cwv metric="cls" value={pagespeed.field.cls} label="CLS" />
									<div className={cn('cwvNote')}>Dữ liệu thực tế người dùng (CrUX, 28 ngày).</div>
								</>
							) : (
								<>
									<Cwv metric="lcp" value={pagespeed.lab.lcp} label="LCP (lab)" />
									<Cwv metric="cls" value={pagespeed.lab.cls} label="CLS (lab)" />
									<Cwv metric="inp" value={pagespeed.lab.tbt} label="TBT (lab)" raw={`${fmt(pagespeed.lab.tbt)}ms`} />
									<div className={cn('cwvNote')}>Chưa đủ dữ liệu thực tế — hiển thị số đo phòng lab.</div>
								</>
							)}
						</div>
					</div>
					<div className={cn('cardFoot')}>
						<span>Đo lúc {pagespeed.created}</span>
						{canPageSpeed && deepLink('/pagespeed', 'Đo lại / xem chi tiết')}
					</div>
					</>
				) : (
					<EmptyHint icon="fa-light fa-gauge-high"
						text="Chưa có báo cáo PageSpeed cho website này. Chạy công cụ PageSpeed / Core Web Vitals để đo tốc độ tải trang."
						to={canPageSpeed && deepLink('/pagespeed', 'Đo PageSpeed')} />
				)}
			</div>

			{/* ---- Traffic tìm kiếm (từ Google Search Console) ---- */}
			<h3 className={cn('sectionTitle')}>Hiệu suất tìm kiếm (Google Search Console)</h3>
			<div className={cn('card')}>
				{gsc?.connected && gsc?.has_data ? (
					<>
						<div className={cn('gscSummary')}>
							<GscMetric label="Clicks" value={fmt(gsc.summary.clicks)} delta={gsc.delta.clicks} />
							<GscMetric label="Impressions" value={fmt(gsc.summary.impressions)} delta={gsc.delta.impressions} />
							<GscMetric label="CTR" value={`${(gsc.summary.ctr * 100).toFixed(1)}%`} delta={gsc.delta.ctr} />
							<GscMetric label="Vị trí TB" value={gsc.summary.position.toFixed(1)} delta={gsc.delta.position} />
						</div>
						<GscTrendChart series={gsc.series} />
						<div className={cn('cardFoot')}>
							<span>28 ngày gần nhất ({gsc.range.from} → {gsc.range.to})</span>
							{canGsc && deepLink(`/projects/${projectId}/gsc`, 'Mở báo cáo GSC đầy đủ')}
						</div>
					</>
				) : gsc?.connected ? (
					<EmptyHint icon="fa-light fa-rotate"
						text="Đã kết nối GSC — dữ liệu đang được đồng bộ trong nền. Quay lại sau ít phút để xem hiệu suất tìm kiếm."
						to={canGsc && deepLink(`/projects/${projectId}/gsc`, 'Xem tiến độ đồng bộ')} />
				) : (
					<EmptyHint icon="fa-light fa-magnifying-glass-chart"
						text="Chưa kết nối Google Search Console. Kết nối để theo dõi click, impression, CTR và thứ hạng từ khoá thực tế."
						to={canGsc && deepLink(`/projects/${projectId}/gsc`, 'Kết nối GSC')} />
				)}
			</div>
		</div>
	);
}

/** Một chỉ số CWV: giá trị + màu theo ngưỡng. */
function Cwv({metric, value, label, raw}) {
	const color = cwvColor(metric, value);
	const display = raw != null ? raw : (value === null || value === undefined ? '—' : CWV[metric].fmt(value));
	return (
		<div className={cn('cwvChip')}>
			<div className={cn('cwvValue')} style={{color}}>{display}</div>
			<div className={cn('cwvLabel')}>{label}</div>
		</div>
	);
}

/** Một thẻ chỉ số GSC + % thay đổi. */
function GscMetric({label, value, delta}) {
	return (
		<div className={cn('gscMetric')}>
			<div className={cn('gscMetricLabel')}>{label}</div>
			<div className={cn('gscMetricValue')}>{value}</div>
			<Delta value={delta} />
		</div>
	);
}

export default ProjectOverview;
